import { Authenticator, CheckboxField, useAuthenticator } from '@aws-amplify/ui-react'
import { Stack } from '@tumelo/designsystem'
import { Amplify, ResourcesConfig } from 'aws-amplify'
import { signUp } from 'aws-amplify/auth'
import { useRouter } from 'next/router'
import React, { use, useMemo } from 'react'
import styled from 'styled-components'
import { AmplifyStyles } from './AmplifyStyles'
import '@aws-amplify/ui-react/styles.css'
import { ConfigContext, getConfig } from './environment'
import { SentryStyles } from './SentryStyles'

interface AuthProps {
  children: React.ReactNode
}

export const Auth: React.FC<AuthProps> = ({ children }) => {
  const { userpoolId, appClientId } = use(ConfigContext)
  const config = getConfig()
  const router = useRouter()
  const publicPaths: string[] = ['/legal/cookies-policy']

  useMemo(() => {
    const authConfig: ResourcesConfig = {
      Auth: {
        Cognito: {
          userPoolId: userpoolId,
          userPoolClientId: appClientId,
        },
      },
    }

    Amplify.configure(authConfig)
    return Auth
  }, [userpoolId, appClientId])

  if (publicPaths.includes(router.pathname)) {
    return <>{children}</>
  }
  if (config.clientType === 'mock') {
    return <>{children}</>
  }

  return (
    <Authenticator.Provider>
      <SentryStyles />
      <AmplifyStyles />
      <div>
        <Authenticator
          loginMechanisms={['email']}
          signUpAttributes={['email', 'family_name', 'given_name']}
          formFields={{
            signUp: {
              email: {
                isRequired: true,
                order: 1,
              },
              password: {
                isRequired: true,
                order: 2,
              },
              confirm_password: {
                isRequired: true,
                order: 3,
              },
              given_name: {
                label: 'First Name',
                placeholder: 'Enter your First Name',
                isRequired: true,
                order: 4,
              },
              family_name: {
                label: 'Last Name',
                placeholder: 'Enter your Last Name',
                isRequired: true,
                order: 5,
              },
            },
          }}
          components={{
            Header() {
              return (
                <Stack alignItems="center" mb="3.2rem">
                  <img alt="tumelo" src="/images/Main_Logo_Tumelo_White-min.png" width="250" height="55" />
                </Stack>
              )
            },

            SignUp: {
              FormFields() {
                const { validationErrors } = useAuthenticator()
                return (
                  <>
                    {/* Re-use default `Authenticator.SignUp.FormFields` */}
                    <Authenticator.SignUp.FormFields />

                    {/* Append & require acknowledgement fields to sign up */}
                    <CheckboxField
                      errorMessage={validationErrors.termsAndConditionsMessage as string}
                      hasError={!validationErrors.termsAndConditionsValid}
                      name="terms_and_conditions"
                      value="yes"
                      label={
                        <>
                          I agree with the{' '}
                          <StyledAnchor href="https://www.tumelo.com/terms-and-conditions" target="_blank">
                            Terms &#38; Conditions
                          </StyledAnchor>
                        </>
                      }
                    />
                    <CheckboxField
                      errorMessage={validationErrors.privacyPolicyMessage as string}
                      hasError={!validationErrors.privacyPolicyValid}
                      name="privacy_policy"
                      value="yes"
                      label={
                        <>
                          I agree with the{' '}
                          <StyledAnchor href="https://www.tumelo.com/privacy-policy" target="_blank">
                            Privacy Policy
                          </StyledAnchor>
                        </>
                      }
                    />
                  </>
                )
              },
            },
          }}
          services={{
            async handleSignUp(input) {
              return signUp({
                ...input,
                options: {
                  ...input.options,
                  userAttributes: {
                    ...input.options?.userAttributes,
                    phone_number: '+447700900000',
                  },
                },
              })
            },
            async validateCustomSignUp(formData, touchData) {
              if (formData.terms_and_conditions && formData.privacy_policy) {
                return
              }
              return {
                termsAndConditionsValid: formData.terms_and_conditions,
                termsAndConditionsMessage:
                  touchData.terms_and_conditions && !formData.terms_and_conditions
                    ? 'You must agree to the Terms & Conditions'
                    : '',
                privacyPolicyValid: formData.privacy_policy,
                privacyPolicyMessage:
                  touchData.privacy_policy && !formData.privacy_policy ? 'You must agree to the Privacy Policy' : '',
              }
            },
          }}
        >
          {children}
        </Authenticator>
      </div>
    </Authenticator.Provider>
  )
}

const StyledAnchor = styled.a`
  text-decoration: underline;
`
